import React from 'react';

import {
    MyLearningPage,
    CookiePolicyPage,
    CategoryDetailsPage,
    CurriculumDetailsPage,
    CourseDetailsPage,
    VideoPage,
    NotFoundPage,
    NoAuthPage,
    LoginRouter,
} from '../components/pages';
import {
    redirectIfDifferentBrand,
    redirectIfMissingTags
} from './routeRedirectRules';
import { IS_INTERNAL_SITE } from '../const/segmentation';
import { SearchLibrary } from '../components/pages/searchLibrary/SearchLibrary';
import Progress from '../components/pages/progress/Progress';
import { Goodbye } from '../components/pages/goodbye/Goodbye';
import { IS_SEIKO_THEME } from '../branding';
import { evaluateRouteRedirect } from './utils';
import { CategoryRoutingPage } from '../components/pages/categoryRoutingPage/CategoryRoutingPage';
import { PAGE_LEVEL } from '../breadcrumbs/BreadcrumbContext';
import { MyLearningTranscript } from '../components/pages/learningTranscript/MyLearningTranscript';

export class Routes {

    static LoginRouter = {
        routeData: {
            pattern: '/login-redirect/:loginType?',
            render: ( props ) => <LoginRouter { ...props } />
        },
        buildPath: ( loginType ) => `login-redirect/${ loginType }`
    }

    static CookiePolicy = {
        routeData: { 
            key: 'cookie-policy',
            documentTitle: 'Cookie Policy',
            translationKey: 'application.windowTitle.cookiePolicy',
            route: '/cookie-policy',
            pattern: '/cookie-policy/:locale?',
            render: ( props ) => <CookiePolicyPage { ...props } pageKey={ 'cookie-policy' } />,
            hideCookieAlert: true,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() ),
            ]
        },
        buildPath: () => '/cookie-policy'
    };

    static Home = {
        routeData: { 
            key: 'home',
            documentTitle: 'My Progress', 
            translationKey: 'application.windowTitle.progress',
            route: '/',
            pattern: '/',
            render: ( props ) => <Progress { ...props } pageKey={ 'home' } />,
            hideCookieAlert: false,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() ),
                () => IS_SEIKO_THEME ? evaluateRouteRedirect( Routes.CategoryRouter.buildPath() ) : undefined,
            ],
        },
        buildPath: () => '/',
    };

    static CourseDetails = {
        routeData: { 
            key: 'course-details',
            documentTitle: 'Course Details',
            translationKey: 'application.windowTitle.courseDetails',
            pageLevel: PAGE_LEVEL.SECOND_LEVEL,
            route: '/course',
            pattern: '/course/:courseId',
            render: ( props ) => <CourseDetailsPage { ...props }  pageKey={ 'course-details' } />,
            hideCookieAlert: false,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() )
            ]
        },
        buildPath: ( courseId ) => `/course/${ courseId }`
    };

    static CurriculumDetails = {
        routeData: { 
            key: 'curriculum-details',
            documentTitle: 'Curriculum Details',
            translationKey: 'application.windowTitle.curriculumDetails',
            pageLevel: PAGE_LEVEL.FIRST_LEVEL,
            route: '/curriculum',
            pattern: '/curriculum/:curriculumId',
            render: ( props ) => <CurriculumDetailsPage { ...props }  pageKey={ 'curriculum-details' } />,
            hideCookieAlert: false,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() )
            ]
        },
        buildPath: ( curriculumId ) => `/curriculum/${ curriculumId }`
    };

    static CategoryDetails = {
        routeData: { 
            key: 'category-details',
            documentTitle: 'Category Details',
            translationKey: 'application.windowTitle.categoryDetails',
            pageLevel: PAGE_LEVEL.TOP_LEVEL,
            route: '/category',
            pattern: '/category/:categoryId',
            render: ( props ) => <CategoryDetailsPage { ...props }  pageKey={ 'category-details' } />,
            hideCookieAlert: false,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() )
            ]
        },
        buildPath: ( categoryId ) => `/category/${ categoryId }`
    };

    static CategoryRouter = {
        routeData: { 
            key: 'category-router',
            documentTitle: 'Category',
            translationKey: 'application.windowTitle.categoryDetails',
            pattern: '/category',
            render: ( props ) => <CategoryRoutingPage { ...props } pageKey={ 'category-router' } />,
            hideCookieAlert: false,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() )
            ]
        },
        buildPath: () => '/category'
    }

    static VideoPlayer = {
        routeData: { 
            key: 'video-player',
            documentTitle: 'Video Player',
            translationKey: 'application.windowTitle.videoPlayer',
            pattern: '/video-player/:courseId/:learningObjectId',
            render: ( props ) => <VideoPage { ...props } pageKey={ 'video-player' } />,
            hideCookieAlert: false,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() )
            ]
        },
        buildPath: ( courseId, learningObjectId ) => `/video-player/${ courseId }/${ learningObjectId }`
    };

    static Search = {
        routeData: {
            key: 'search',
            documentTitle: 'Search Library',
            translationKey: 'application.windowTitle.searchLibrary',
            pageLevel: PAGE_LEVEL.TOP_LEVEL,
            route: '/search',
            pattern: '/search',
            render: props => <SearchLibrary { ...props }  pageKey={ 'search' } />,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() )
            ]
        },
        buildPath: () => '/search',
    };

    static Learning = {
        routeData: {
            key: 'learning',
            documentTitle: 'Learning Center',
            translationKey: 'application.windowTitle.myLearning', 
            pageLevel: PAGE_LEVEL.TOP_LEVEL,
            route: '/learning',
            pattern: '/learning',
            render: props => <MyLearningPage { ...props }  pageKey={ 'learning' } />,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() ),
            ]
        },
        buildPath: () => '/learning',
    };

    static LearningTranscript = {
        routeData: {
            key: 'transcript',
            documentTitle: 'My Learning Transcript ',
            translationKey: 'application.windowTitle.myLearningTranscript', 
            pageLevel: PAGE_LEVEL.TOP_LEVEL,
            route: '/transcript',
            pattern: '/transcript',
            render: props => <MyLearningTranscript { ...props }  pageKey={ 'transcript' } />,
            redirect: () => [
                ( IS_INTERNAL_SITE
                    ? redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'employee' ] )
                    : redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'customer' ] )
                ),
                redirectIfMissingTags( Routes.Unauthorized.buildPath(), [ 'is_portal_user' ] ),
                redirectIfDifferentBrand( Routes.Unauthorized.buildPath() ),
            ]
        },
        buildPath: () => '/transcript',
    };

    static Goodbye = {
        routeData: {
            key: 'goodbye',
            documentTitle: 'Rerouting...',
            pattern: '/goodbye',
            render: () => <Goodbye pageKey={ 'goodbye' } />,
            redirect: undefined,
            hideCookieAlert: true,
        },
        buildPath: () => '/goodbye'
    };

    static Unauthorized = {
        routeData: { 
            key: 'unauthorized',
            documentTitle: 'Unauthorized',
            pattern: '/unauthorized',
            render: ( props ) => <NoAuthPage { ...props } pageKey={ 'unauthorized' } />,
            hideCookieAlert: true,
            redirect: undefined,
        },
        buildPath: () => '/unauthorized',
    };

    static NotFoundError = {
        routeData: { 
            key: 'page-not-found',
            documentTitle: 'Page Not Found',
            pattern: '/(.*)',
            render: ( props ) => <NotFoundPage { ...props } pageKey={ 'page-not-found' } />,
            hideCookieAlert: true,
            redirect: undefined,
        },
        buildPath: () => '/404',
    };
}

export const getRouteFromUrl = ( url ) => {
    const testUrl = url.startsWith( '/' ) ? url : `/${ url }`;

    return routeList.findLast( r => testUrl.includes( r.route ) );
};

/**
 * Build a list of the route data to use with the `biw-web-router`. Note
 * that the order of these matter. As new routes are being evaluated,
 * patterns will be checked starting at the top of the list.
 */
export const routeList = [
    Routes.Home.routeData,
    Routes.CookiePolicy.routeData,
    Routes.CategoryDetails.routeData,
    Routes.CategoryRouter.routeData,
    Routes.CurriculumDetails.routeData,
    Routes.CourseDetails.routeData, 
    Routes.VideoPlayer.routeData,
    Routes.LoginRouter.routeData,
    Routes.Search.routeData,
    Routes.Learning.routeData,
    Routes.LearningTranscript.routeData,
    Routes.Goodbye.routeData,
    Routes.Unauthorized.routeData,
    Routes.NotFoundError.routeData,
];

/**
 * Retrieves the route details given the path.
 * 
 * @param { string } key - The page key.
 */
export function getRouteDetails( key ) {
    return routeList.find( r => r.key === key );
}

/**
 * The path to redirect the user to once they are signed in.
 */
export const DEFAULT_SIGN_IN_REDIRECT_PATH = Routes.Home.buildPath();
